import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { getLandingPageBySlug } from "../../data/landingPagesConfig";
import { getProfession } from "../../data/startPageContent";
import { FOR_HERO, FOR_FEATURES, FOR_PROFESSIONS, fill } from "../../data/forPageContent";
import usePageSeo from "../../hooks/usePageSeo";
import useUtmParams from "../../hooks/useUtmParams";
import StartHeader from "../../components/start/StartHeader";
import StartFooter from "../../components/start/StartFooter";
import StartFeatureRows from "../../components/start/StartFeatureRow";
import StartStats from "../../components/start/StartStats";
import StartSecurity from "../../components/start/StartSecurity";
import StartFaq from "../../components/start/StartFaq";
import StartClose from "../../components/start/StartClose";
import StartStickyBar from "../../components/start/StartStickyBar";
import ForHero from "../../components/for/ForHero";
import ForSpecializations from "../../components/for/ForSpecializations";
import ForFeatures from "../../components/for/ForFeatures";
import ForTestimonials from "../../components/for/ForTestimonials";
import ForProfessions from "../../components/for/ForProfessions";
import useJsonLd from "../../components/for/useJsonLd";
import { buildForJsonLd } from "../../components/for/forJsonLd";
import "../../components/start/startReveal.css";
import "../start/StartPage.css";

/** "מערכת ניהול קליניקה לקלינאות תקשורת" → profession name in mint (<em>). */
function ProfessionTitle({ title, profession }) {
  const idx = profession ? title.indexOf(profession) : -1;
  if (idx === -1) return title;
  return (
    <>
      {title.slice(0, idx)}
      <em>{title.slice(idx, idx + profession.length)}</em>
      {title.slice(idx + profession.length)}
    </>
  );
}

/**
 * /for/:slug — profession landing page on the /start design.
 * Same shell (StartHeader / dark hero + lead form / feature rows / stats /
 * security / FAQ / closing form / footer / sticky bar), plus the
 * profession-specific sections from landingPagesConfig.
 */
function ClinicTypeLandingPage() {
  const { slug } = useParams();
  const pageData = getLandingPageBySlug(slug);
  const professionKey = getProfession(slug)?.key || null;
  const utm = useUtmParams();

  usePageSeo({
    title: pageData?.metaTitle || "CliniKit – מערכת ניהול קליניקות למטפלים",
    description: pageData?.metaDescription || "",
    path: `/for/${slug}`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useJsonLd("clinic-landing-jsonld", () => (pageData ? buildForJsonLd(pageData) : []), [slug]);

  if (!pageData) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="st-page" dir="rtl">
      <StartHeader />

      <main className="st-main" data-profession={professionKey || ""}>
        <ForHero
          crumb={pageData.profession}
          overline={fill(FOR_HERO.overline, pageData)}
          title={<ProfessionTitle title={pageData.heroTitle} profession={pageData.profession} />}
          sub={pageData.heroSubtitle}
          badge={pageData.profession}
          emoji={pageData.emoji}
          profession={professionKey}
          utm={utm}
        />

        <ForSpecializations pageData={pageData} />

        <ForFeatures
          overline={FOR_FEATURES.overline}
          title={fill(FOR_FEATURES.title, pageData)}
          intro={pageData.intro}
          items={pageData.features}
        />

        <StartFeatureRows profession={professionKey} />
        <StartStats />
        <ForTestimonials pageData={pageData} />
        <StartSecurity />
        <ForProfessions
          overline={fill(FOR_PROFESSIONS.overline, pageData)}
          title={FOR_PROFESSIONS.title}
          sub={FOR_PROFESSIONS.sub}
          currentSlug={slug}
        />
        <StartFaq />
        <StartClose profession={professionKey} utm={utm} />
      </main>

      <StartFooter />
      <StartStickyBar />
    </div>
  );
}

export default ClinicTypeLandingPage;
